import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { SiteIdentity } from '../components/SiteIdentity';
import { GoogleButton, useOauthError } from '../components/GoogleButton';
import type { User } from '../types';

interface Props {
  onLogin: (user: User) => void;
  onSwitch: () => void;
  onForgot: () => void;
}

/** Xəta kodlarını istifadəçinin başa düşdüyü dilə çeviririk. */
function messageFor(code: string, body: Record<string, unknown> = {}): string {
  switch (code) {
    case 'invalid':
      return 'E-poçt və ya şifrə yanlışdır.';
    case 'too_many_attempts': {
      // ⚠️ BU MESAJ ÜMUMİ OLA BİLMƏZ.
      //
      // Sürət limitinə düşən istifadəçi DOĞRU şifrə yazsa da girə bilmir.
      // «Giriş alınmadı» desək, o, şifrəsinin səhv olduğunu düşünüb
      // təkrar-təkrar cəhd edər — hər cəhd blok müddətini uzadır və
      // istifadəçi çıxılmaz dövrəyə düşür. Səbəb və müddət deyilməlidir.
      const sec = Number(body['retryAfter'] ?? 0);
      const min = Math.ceil(sec / 60);
      return sec > 0
        ? `Çox sayda uğursuz cəhd oldu. Təhlükəsizlik üçün giriş ${min} dəqiqə bağlıdır — bu müddətdə doğru şifrə də qəbul edilmir.`
        : 'Çox sayda uğursuz cəhd oldu. Təhlükəsizlik üçün giriş müvəqqəti bağlıdır.';
    }
    case 'suspended':
      return 'Hesab dayandırılıb. Abunənizi yoxlayın.';
    case 'totp_required':
      return 'İki mərhələli təsdiq tələb olunur.';
    case 'email_and_password_required':
      return 'E-poçt və şifrə boş ola bilməz.';
    default:
      return 'Giriş alınmadı. Bir azdan yenidən yoxlayın.';
  }
}

export function Login({ onLogin, onSwitch, onForgot }: Props): React.ReactElement {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const oauthError = useOauthError();

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user } = await api.login(email, password);
      onLogin(user);
    } catch (err) {
      setError(
        err instanceof ApiError ? messageFor(err.code, err.body) : messageFor('unknown'),
      );
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <form className="login-card" onSubmit={submit}>
        <div className="login-brand">
          <span className="login-mark">M</span>
          <span className="login-name">Mailora</span>
        </div>
        <p className="login-lead">Şirkət poçtunuza daxil olun</p>

        <label className="field">
          <span>E-poçt ünvanı</span>
          <input
            type="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ad@sirketiniz.az"
          />
        </label>

        <label className="field">
          <span>Şifrə</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        <p className="login-forgot">
          <button type="button" className="linkish" onClick={onForgot}>
            Şifrəni unutmusunuz?
          </button>
        </p>

        {(error || oauthError) && (
          <p className="login-error" role="alert">
            {error ?? oauthError}
          </p>
        )}

        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Yoxlanılır…' : 'Daxil ol'}
        </button>

        <GoogleButton />

        <p className="login-alt">
          Hesabınız yoxdur?{' '}
          <button type="button" className="linkish" onClick={onSwitch}>
            Şirkət hesabı yaradın
          </button>
        </p>

        <SiteIdentity />
      </form>
    </div>
  );
}
